import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HelpCircle, Pencil } from "lucide-react";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/Button";

/**
 * „Informacje dla koordynatorów” na karcie umowy ramowej — wewnętrzna notatka dla
 * zamawiających (np. „przyjmuje WPW tylko razem z MDCP”). Megamapa podwykonawców pokazuje
 * ją pod ikoną „?” przy firmie; pusta = brak ikony. Edytuje krąg „Podwykonawcy i umowy
 * ramowe” (ten sam, co edycja umowy — backend CanManageContractors); konto podwykonawcy
 * nigdy jej nie dostaje (backend wycina pole).
 */
export function AgreementCoordinatorInfoSection({
  agreementId,
  value,
  canManage,
}: {
  agreementId: string;
  value: string;
  canManage: boolean;
}) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (coordinator_info: string) =>
      apiClient.patch(`/agreements/${agreementId}/`, { coordinator_info }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agreement", agreementId] });
      qc.invalidateQueries({ queryKey: ["contractors", "area-map"] });
      setEditing(false);
      setError(null);
    },
    onError: () => setError("Nie udało się zapisać informacji dla koordynatorów."),
  });

  const startEdit = () => {
    setDraft(value);
    setError(null);
    setEditing(true);
  };

  return (
    <div
      className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
      data-testid="agreement-coordinator-info"
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
          <HelpCircle size={15} className="text-gray-400 dark:text-gray-500" />
          Informacje dla koordynatorów
        </h3>
        {canManage && !editing && (
          <Button size="sm" variant="secondary" onClick={startEdit}>
            <Pencil size={13} /> {value ? "Edytuj" : "Dodaj"}
          </Button>
        )}
      </div>
      <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
        Notatka dla zamawiających — pokazuje się pod ikoną „?” przy firmie na megamapie
        podwykonawców. Podwykonawca jej nie widzi.
      </p>

      {editing ? (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={3}
            maxLength={1000}
            aria-label="Informacje dla koordynatorów"
            placeholder="np. Przyjmuje WPW tylko razem z MDCP na tym samym projekcie."
            className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm text-gray-900 dark:text-gray-100"
          />
          {error && <p className="text-xs text-red-600">{error}</p>}
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => save.mutate(draft.trim())} disabled={save.isPending}>
              {save.isPending ? "Zapisywanie…" : "Zapisz"}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>
              Anuluj
            </Button>
          </div>
        </div>
      ) : value ? (
        <p className="whitespace-pre-line text-sm text-gray-800 dark:text-gray-200">{value}</p>
      ) : (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak — ikona „?” nie pokazuje się na mapie.</p>
      )}
    </div>
  );
}
